import { Dialog, Table, Loader } from '@cloudflare/kumo'
import type { ServerDetail, AccountDetail, MatrixCell } from '../types'
import { StatusBadge } from './StatusBadge'
import { fmtPct, fmtNum, relativeTime } from '../health'

function MiniStat({
  label,
  value,
  tone,
}: {
  label: string
  value: number | string
  tone?: 'danger' | 'warning'
}) {
  const valueClass =
    tone === 'danger'
      ? 'text-kumo-danger'
      : tone === 'warning'
        ? 'text-kumo-warning'
        : 'text-kumo-strong'
  return (
    <div className="flex-1 min-w-[110px] rounded-lg border border-kumo-hairline bg-kumo-elevated px-3.5 py-3">
      <span className="text-xs uppercase font-semibold text-kumo-subtle">{label}</span>
      <div className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</div>
    </div>
  )
}

function CellTable({
  cells,
  primary,
}: {
  cells: MatrixCell[]
  primary: 'account' | 'server'
}) {
  if (cells.length === 0) {
    return (
      <div className="py-8 text-center">
        <span className="text-kumo-subtle">No connections.</span>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <Table layout="auto">
        <Table.Header>
          <Table.Row>
            <Table.Head>{primary === 'account' ? 'Customer' : 'Server'}</Table.Head>
            <Table.Head>Status</Table.Head>
            <Table.Head>Connection</Table.Head>
            <Table.Head>Error Rate</Table.Head>
            <Table.Head>Req/min</Table.Head>
            <Table.Head>Latency</Table.Head>
            <Table.Head>Issues</Table.Head>
            <Table.Head>Last Seen</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {cells.map((c) => (
            <Table.Row key={`${c.account_id}|${c.server_id}`}>
              <Table.Cell>
                <div className="font-semibold text-kumo-default">
                  {primary === 'account' ? c.account_name : c.server_name}
                </div>
                <div className="text-[11px] text-kumo-subtle font-mono">
                  {primary === 'account' ? `${c.account_id.slice(0, 16)}…` : c.provider}
                </div>
              </Table.Cell>
              <Table.Cell>
                <StatusBadge health={c.health} />
              </Table.Cell>
              <Table.Cell>
                <span className="capitalize text-kumo-strong">{c.connection_status}</span>
              </Table.Cell>
              <Table.Cell>
                <span
                  className={
                    c.error_rate_pct && c.error_rate_pct >= 5
                      ? 'text-kumo-danger'
                      : 'text-kumo-strong'
                  }
                >
                  {fmtPct(c.error_rate_pct)}
                </span>
              </Table.Cell>
              <Table.Cell>{fmtNum(c.requests_per_min)}</Table.Cell>
              <Table.Cell>
                {c.avg_latency_ms === null ? '—' : `${c.avg_latency_ms.toFixed(0)}ms`}
              </Table.Cell>
              <Table.Cell>
                <span className={c.issue_count > 0 ? 'text-kumo-danger' : 'text-kumo-strong'}>
                  {c.issue_count}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className="text-kumo-subtle">{relativeTime(c.last_seen)}</span>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}

function ServerBody({ detail }: { detail: ServerDetail }) {
  const { server, summary, affected_accounts, issues_by_type } = detail
  return (
    <>
      <Dialog.Title>
        <span className="inline-flex items-center gap-2.5">
          {server.server_name}
          <StatusBadge health={summary.health} />
        </span>
      </Dialog.Title>
      <p className="mt-1 text-sm text-kumo-subtle">
        {server.provider} · Customers affected by this server
      </p>
      <div className="flex flex-wrap gap-3 my-5">
        <MiniStat label="Connected Accounts" value={summary.total_connected_accounts} />
        <MiniStat
          label="Blocked"
          value={summary.blocked_accounts}
          tone={summary.blocked_accounts > 0 ? 'danger' : undefined}
        />
        <MiniStat
          label="Error"
          value={summary.error_accounts}
          tone={summary.error_accounts > 0 ? 'warning' : undefined}
        />
        {issues_by_type.map((i) => (
          <MiniStat key={i.issue_type} label={i.issue_type} value={i.count} />
        ))}
      </div>
      <CellTable cells={affected_accounts} primary="account" />
    </>
  )
}

function AccountBody({ detail }: { detail: AccountDetail }) {
  const { account, summary, connected_servers } = detail
  return (
    <>
      <Dialog.Title>
        <span className="inline-flex items-center gap-2.5">
          {account.account_name}
          <StatusBadge health={summary.health} />
        </span>
      </Dialog.Title>
      <p className="mt-1 text-sm text-kumo-subtle font-mono">
        {account.account_id} · Servers this customer connects to
      </p>
      <div className="flex flex-wrap gap-3 my-5">
        <MiniStat label="Connected Servers" value={summary.total_connected_servers} />
        <MiniStat
          label="Blocked"
          value={summary.blocked_servers}
          tone={summary.blocked_servers > 0 ? 'danger' : undefined}
        />
        <MiniStat
          label="Error"
          value={summary.error_servers}
          tone={summary.error_servers > 0 ? 'warning' : undefined}
        />
      </div>
      <CellTable cells={connected_servers} primary="server" />
    </>
  )
}

export function DetailDrawer({
  open,
  onClose,
  loading,
  error,
  server,
  account,
}: {
  open: boolean
  onClose: () => void
  loading: boolean
  error: string | null
  server: ServerDetail | null
  account: AccountDetail | null
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog size="xl" className="p-6">
        {loading && (
          <div className="py-16 flex justify-center">
            <Loader size={24} />
          </div>
        )}
        {error && !loading && (
          <p className="py-8 block text-center text-kumo-danger">{error}</p>
        )}
        {!loading && !error && server && <ServerBody detail={server} />}
        {!loading && !error && account && <AccountBody detail={account} />}
      </Dialog>
    </Dialog.Root>
  )
}
